'use client';

import { type ReactNode } from 'react';

import type { BioContext } from '@ps/functions/bio/bioContext';
import { Share2 } from 'lucide-react';

import { type ShareTarget, shareUrlForTarget } from './shareTarget';
import { useShareDialog } from './useShareDialog';

interface Props {
  /** The page's bio context — makes the built link custom-domain aware. */
  bioContext: BioContext;
  /** What to share, as contextual facts (id / slug); the URL is built here. */
  target: ShareTarget;
  /** Native-share + dialog title — usually the entity name. */
  title: string;
  /** Merged onto the `<button>` so each host owns its look. */
  className?: string;
  /** Accessible label; defaults to `"Share"`. */
  'aria-label'?: string;
  /** The glyph; defaults to a `Share2` icon. */
  children?: ReactNode;
}

/**
 * The one client share control for every bio surface. Builds the canonical,
 * custom-domain-aware share URL for its {@link ShareTarget} (via
 * {@link shareUrlForTarget}) and opens the app-wide `ShareModal` through
 * {@link useShareDialog} — so no caller hand-assembles a `${origin}/…` link or
 * calls `navigator.share` directly. Layout-agnostic: the host supplies the
 * button styling (`className`) and, optionally, the icon (`children`).
 */
export function ShareButton({
  bioContext,
  target,
  title,
  className,
  'aria-label': ariaLabel = 'Share',
  children,
}: Props) {
  const { openShare } = useShareDialog();

  return (
    <button
      type="button"
      onClick={() =>
        openShare({ url: shareUrlForTarget(bioContext, target), title })
      }
      aria-label={ariaLabel}
      className={className}
    >
      {children ?? <Share2 className="h-5 w-5" aria-hidden />}
    </button>
  );
}
